import { formatInTimeZone } from 'date-fns-tz';

const dateFormat = 'yyyy-MM-dd';
const dateTimeFormat = 'yyyy-MM-dd HH:mm:ss';

const applyTimezoneToDate = (dateValue: Date | null | undefined, timezone: string): Date | null => {
  if (!dateValue) return null;

  const dateStr = formatInTimeZone(dateValue, timezone, dateTimeFormat);

  return new Date(dateStr);
};

const getMinTimeRestrictions = (
  minDate: Date | null,
  selectedDate: Date,
  timezone: string,
  selectedHour: number
): { minHours: number; minMinutes: number } => {
  if (!minDate) return { minHours: 0, minMinutes: 0 };

  const selectedDateInTz = formatInTimeZone(selectedDate, timezone, dateFormat);
  const minDateInTz = formatInTimeZone(minDate, timezone, dateFormat);

  if (selectedDateInTz !== minDateInTz) {
    return { minHours: 0, minMinutes: 0 };
  }

  const minHours = parseInt(formatInTimeZone(minDate, timezone, 'H'), 10);
  const minMinutes = selectedHour === minHours 
    ? parseInt(formatInTimeZone(minDate, timezone, 'm'), 10)
    : 0;

  return { minHours, minMinutes };
};

const getMaxTimeRestrictions = (
  maxDate: Date | null,
  selectedDate: Date,
  timezone: string,
  selectedHour: number
): { maxHours: number; maxMinutes: number } => {
  if (!maxDate) return { maxHours: 23, maxMinutes: 59 };

  const selectedDateInTz = formatInTimeZone(selectedDate, timezone, dateFormat);
  const maxDateInTz = formatInTimeZone(maxDate, timezone, dateFormat);

  if (selectedDateInTz !== maxDateInTz) {
    return { maxHours: 23, maxMinutes: 59 };
  }

  const maxHours = parseInt(formatInTimeZone(maxDate, timezone, 'H'), 10);
  const maxMinutes = selectedHour === maxHours
    ? parseInt(formatInTimeZone(maxDate, timezone, 'm'), 10)
    : 59;

  return { maxHours, maxMinutes };
};

type TimeRestrictionsParams = {
  minDate: Date;
  maxDate: Date;
  selectedDate: Date;
  timezone: string;
  selectedHour: number;
};

const useTimeRestrictions = ({
  minDate,
  maxDate,
  selectedDate,
  timezone,
  selectedHour,
}: TimeRestrictionsParams): {
  minHours: number;
  minMinutes: number;
  maxHours: number;
  maxMinutes: number;
  minDateForPicker: Date | null;
  maxDateForPicker: Date | null;
} => {
  if (!selectedDate) {
    return {
      minHours: 0,
      minMinutes: 0,
      maxHours: 23,
      maxMinutes: 59,
      minDateForPicker: minDate ? applyTimezoneToDate(minDate, timezone) : null,
      maxDateForPicker: maxDate ? applyTimezoneToDate(maxDate, timezone) : null,
    };
  }

  const { minHours, minMinutes } = getMinTimeRestrictions(minDate, selectedDate, timezone, selectedHour);
  const { maxHours, maxMinutes } = getMaxTimeRestrictions(maxDate, selectedDate, timezone, selectedHour);

  return {
    minHours,
    minMinutes,
    maxHours,
    maxMinutes,
    minDateForPicker: minDate ? applyTimezoneToDate(minDate, timezone) : null,
    maxDateForPicker: maxDate ? applyTimezoneToDate(maxDate, timezone) : null,
  };
};

export default useTimeRestrictions;
